import {
  trackTauriFailedToRelaunch,
  trackTauriRelaunch,
  trackTauriUpdate,
} from '@/lib/analytics/scribe/tauri';
import { store } from '@/store';
import { systemController } from '@/tauri/controller/system';
import { UpdaterBridgeError } from '@/tauri/updater';
import {
  checkUpdateForFeed,
  installUpdateForFeed,
  resolveUpdaterFeed,
  type UpdaterFeed,
} from '@/tauri/updater/feed';
import { updateFailureMessages } from '@/tauri/updater/updater.messages';
import { isTauri } from '@/tauri/utils';
import { isConfirmedDesktopOfflineNetworkMode } from '@/utils/http/desktopOfflineNetwork';
import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { createModel } from '@rematch/core';
import { getVersion } from '@tauri-apps/api/app';
import { relaunch } from '@tauri-apps/plugin-process';
import { type MessageDescriptor } from 'react-intl';
import { type RootModel } from '.';
// import { RecordingStatus } from '../scribeModel';

/** Undefined when the error did not come through an updater bridge. */
const updaterErrorCode = (error: unknown): string | undefined =>
  error instanceof UpdaterBridgeError ? error.errorCode : undefined;

/**
 * Bounded log context for a caught error. Never the message: plugin errors can
 * embed install paths, which carry the OS username. An unrecognised error logs
 * its message length instead, so different failures can still be told apart.
 */
const updaterErrorContext = (error: unknown) => {
  const errorCode = updaterErrorCode(error);
  const errorName = error instanceof Error ? error.name : typeof error;
  if (errorCode !== undefined && errorCode !== 'UPDATER_UNCLASSIFIED') {
    return { errorCode, errorName };
  }
  const message = error instanceof Error ? error.message : String(error);
  return { errorCode, errorName, messageLength: message.length };
};

/**
 * Failures that retrying the same version cannot fix: every retry re-downloads
 * the whole bundle and fails the same way. The offer is dropped and that
 * version is not offered again this session (`rejected_update` is not
 * persisted, see `tauriUpdateTransform` in `src/store.ts`).
 */
const NON_RETRYABLE_INSTALL_CODES = new Set([
  'UPDATER_SIGNATURE_INVALID',
  'UPDATER_SIGNED_VERSION_MISSING',
  'UPDATER_SIGNED_VERSION_MISMATCH',
  'UPDATER_PREVIOUS_APP_NOT_RESTORED',
]);

/**
 * Codes with a remedy the user owns (unblock a network, get an admin to install)
 * get bespoke copy, and failures a retry cannot fix point at the website
 * download or a reinstall. Everything else (malformed manifest, missing platform
 * asset, unclassified) is ours to fix, so it gets generic retry copy.
 */
const installFailureBody = (errorCode: string | undefined): MessageDescriptor => {
  switch (errorCode) {
    // A Rust-side deadline surfaces as a reqwest failure, i.e. UPDATER_NETWORK —
    // there is no separate frontend timeout code to handle.
    case 'UPDATER_NETWORK':
      return updateFailureMessages.installNetworkFailed;
    case 'UPDATER_INSTALL_PERMISSION_DENIED':
      return updateFailureMessages.installPermissionDenied;
    case 'UPDATER_SIGNATURE_INVALID':
    case 'UPDATER_SIGNED_VERSION_MISSING':
    case 'UPDATER_SIGNED_VERSION_MISMATCH':
      return updateFailureMessages.installVerificationFailed;
    case 'UPDATER_PREVIOUS_APP_NOT_RESTORED':
      return updateFailureMessages.installPreviousAppNotRestored;
    default:
      return updateFailureMessages.installFailed;
  }
};

// todo use tanstack's states

export enum UploadStatus {
  Queued = 'queued',
  Uploading = 'uploading',
  Completed = 'completed',
  Failed = 'failed',
}

export type InitialState = {
  current_version?: string;
  cpu_arch?: string;
  /** A version whose install failed non-retryably; not offered again this session. */
  rejected_update?: { version: string; errorCode: string };
  version_to_update?: {
    version: string;
    body?: string;
    isInstalled: boolean;
    popup_dismissed: boolean;
    // C4: the feed the check resolved. Install reuses it instead of re-reading
    // the gate, so a flag flip between check and install can't reroute a UK/EU
    // client that checked the certified channel onto the global feed.
    feed: UpdaterFeed;
  };
};

const initialState: InitialState = {};

export const tauriUpdateModel = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setCurrentVersion(state, payload: string) {
      return { ...state, current_version: payload };
    },
    setCpuArch(state, payload?: string) {
      return { ...state, cpu_arch: payload };
    },
    setVersionToUpdate(state, payload: InitialState['version_to_update']) {
      return { ...state, version_to_update: payload };
    },
    setRejectedUpdate(state, payload: NonNullable<InitialState['rejected_update']>) {
      return { ...state, rejected_update: payload };
    },
    clear() {
      return initialState;
    },
  },
  effects: (dispatch) => ({
    async getCurrentVersion() {
      if (!isTauri()) {
        return;
      }
      try {
        dispatch.tauriUpdateModel.setCurrentVersion(await getVersion());
      } catch (error) {
        logger.warn('Error reading app version', updaterErrorContext(error));
      }
    },
    async getCpuArchOnce() {
      if (!isTauri()) {
        return;
      }
      if (store.getState().tauriUpdateModel.cpu_arch) return;
      try {
        const arch = await systemController.get_cpu_architecture();
        dispatch.tauriUpdateModel.setCpuArch(arch);
      } catch (error) {
        logger.error('Error fetching CPU arch', updaterErrorContext(error));
        dispatch.tauriUpdateModel.setCpuArch(undefined);
      }
    },
    /**
     * `userInitiated` distinguishes the "Check for updates" button from the
     * background check that fires on mount and on session change. Only the
     * former may raise a toast — surfacing a background failure would interrupt
     * a clinician mid-consult over something they never asked for.
     */
    async handleCheckTauriUpdate(payload?: { userInitiated?: boolean }) {
      const userInitiated = payload?.userInitiated ?? false;
      try {
        if (!isTauri()) {
          return;
        }

        if (isConfirmedDesktopOfflineNetworkMode()) {
          logger.warn('[updater] offline - skipping update check');
          return;
        }

        // The E2E harness drives a pinned build; an "Install & relaunch" toast
        // mid-run only overlays the UI under test.
        if (
          !userInitiated &&
          typeof window !== 'undefined' &&
          window.__IS_E2E__ === true
        ) {
          logger.info('[updater] e2e run - skipping background update check');
          return;
        }

        // The fail-closed routing decision lives in `resolveUpdaterFeed`
        // (APP-6113 §5.19, DC3) — see `src/tauri/updater/feed.ts`.
        const feed = resolveUpdaterFeed();
        if (feed.kind === 'blocked') {
          // Fail-closed: the flag provider hasn't resolved, or the gate is ON
          // but the region is unresolved. Do NO update activity, and clear any
          // stale offer (e.g. from an earlier OFF/global check) so a UK/EU user
          // is never left being shown a stale — possibly global/uncertified —
          // version. Re-runs once the provider/region resolves (the popover
          // effect re-fires).
          logger.warn('[updater] gate/region unresolved — skipping update check');
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
          return;
        }

        const update = await checkUpdateForFeed(feed);
        const { version_to_update: versionToUpdate, rejected_update: rejectedUpdate } =
          store.getState().tauriUpdateModel;
        if (
          update &&
          rejectedUpdate !== undefined &&
          update.version === rejectedUpdate.version
        ) {
          // Logged so a suppressed client can be told apart from an up-to-date one.
          logger.warn('[updater] suppressed a version that failed to install', {
            tag: 'updater_rejected_version_suppressed',
            version: update.version,
            errorCode: rejectedUpdate.errorCode,
            userInitiated,
          });
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
          if (userInitiated) {
            toast({
              tone: 'destructive',
              title: formatMessageOutsideReact(updateFailureMessages.installFailedTitle),
              description: formatMessageOutsideReact(
                installFailureBody(rejectedUpdate.errorCode)
              ),
            });
          }
          return;
        }
        if (update) {
          if (versionToUpdate?.version === update.version) {
            return;
          }
          dispatch.tauriUpdateModel.setVersionToUpdate({
            version: update.version,
            body: update.body,
            popup_dismissed: false,
            isInstalled: false,
            feed, // C4: thread the resolved feed through to install
          });
        } else {
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
        }
      } catch (error) {
        logger.error('[updater] update check failed', {
          tag: 'updater_check_failed',
          ...updaterErrorContext(error),
          userInitiated,
        });
        if (userInitiated) {
          toast({
            tone: 'destructive',
            title: formatMessageOutsideReact(updateFailureMessages.checkFailedTitle),
            description: formatMessageOutsideReact(updateFailureMessages.checkFailed),
          });
        }
      }
    },
    async handleInstallTauriUpdate() {
      if (!isTauri()) {
        return;
      }
      const newUpdate = store.getState().tauriUpdateModel.version_to_update;
      if (!newUpdate) return;

      try {
        // C4: install via the SAME feed the check resolved (threaded on
        // `version_to_update`). Do NOT re-read the gate — a flag flip between
        // check and install must not reroute a UK/EU client that checked the
        // certified channel onto the global feed.
        const installed = await installUpdateForFeed(newUpdate.feed);

        if (installed) {
          dispatch.tauriUpdateModel.setVersionToUpdate({
            ...newUpdate,
            isInstalled: true,
          });
          trackTauriUpdate(newUpdate.version);
          await dispatch.tauriUpdateModel.handleRelaunchApp();
        } else {
          // Not a failure: the feed no longer offers this version (it was
          // pulled, or another install already landed it). Clearing the offer is
          // correct, but it is indistinguishable on screen from a failed
          // install, so log it under its own signature rather than leaving the
          // banner to vanish unexplained.
          logger.warn('[updater] install found no update to apply', {
            tag: 'updater_install_no_update',
            version: newUpdate.version,
          });
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
        }
      } catch (error) {
        const errorCode = updaterErrorCode(error);
        logger.error('[updater] install failed', {
          tag: 'updater_install_failed',
          ...updaterErrorContext(error),
          version: newUpdate.version,
        });
        // Retryable failures keep `version_to_update`: dropping it used to
        // remove the only way back to the button, leaving a manual reinstall.
        // A non-retryable one drops it, since retrying only fails again.
        if (errorCode && NON_RETRYABLE_INSTALL_CODES.has(errorCode)) {
          dispatch.tauriUpdateModel.setRejectedUpdate({
            version: newUpdate.version,
            errorCode,
          });
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
        }
        toast({
          tone: 'destructive',
          title: formatMessageOutsideReact(updateFailureMessages.installFailedTitle),
          description: formatMessageOutsideReact(installFailureBody(errorCode)),
        });
      }
    },

    async handleRelaunchApp() {
      if (!isTauri()) {
        return;
      }
      try {
        trackTauriRelaunch();
        await relaunch();
      } catch (error) {
        trackTauriFailedToRelaunch(String(error));
        logger.error('Failed to relaunch app', updaterErrorContext(error));
      } finally {
        const newUpdate = store.getState().tauriUpdateModel.version_to_update;
        if (newUpdate) {
          dispatch.tauriUpdateModel.setVersionToUpdate({
            ...newUpdate,
            popup_dismissed: true,
          });
        }
      }
    },
  }),
});
